import React, { useCallback, useMemo } from "react";

import { ICellRendererParams } from "@ag-grid-community/core";
import { faMapMarkedAlt } from "@fortawesome/pro-light-svg-icons/faMapMarkedAlt";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import Popover from "antd/es/popover";

import { EntityType } from "@mapmycustomers/shared/enum";
import { GeoManagementState } from "@mapmycustomers/shared/types/base/Located";
import { AnyEntityId } from "@mapmycustomers/shared/types/entity";
import Trip from "@mapmycustomers/shared/types/entity/Trip";

import EntityChip from "@app/component/EntityChip";
import { LocationMiniMapEntity } from "@app/component/field/LocationMiniMap";
import RecordLocationMiniMap from "@app/component/field/LocationMiniMap/RecordLocationMiniMap";
import { LocationValue, TripFieldName } from "@app/util/fieldModel/tripFieldModel";
import { formatCoordinates } from "@app/util/formatters";

import styles from "./TripLocationCellRenderer.module.scss";

const TripLocationCellRenderer: React.FC<ICellRendererParams> = ({
  colDef,
  context,
  data,
  value,
}) => {
  const geoPoint = (value as LocationValue).geoPoint;
  const trip = data as Trip;

  const isStartLocation = colDef.colId === TripFieldName.START;
  const address = isStartLocation ? trip.startAddress : trip.endAddress;
  const entity = isStartLocation ? trip.startEntity : trip.endEntity;

  const mappedEntity: LocationMiniMapEntity = useMemo(() => {
    if (entity) {
      return entity;
    }

    // creating an artificial LocationMiniMapEntity from geoPoint
    return {
      id: Math.random(),
      color: null,
      geoCodedAt: null,
      geoCodeType: null,
      geoManagementState: GeoManagementState.MANUAL,
      geoPoint,
      geoSource: null,
      geoStatus: null,
    };
  }, [entity, geoPoint]);

  const handleClick = useCallback(
    (payload: { entityId: AnyEntityId; entityType: EntityType }) => {
      context.onEntityView?.(payload);
    },
    [context]
  );

  if (!geoPoint) {
    // Wrapping into fragment because this component can't return a plain string.
    // On the other hand it can't return `null` because that's an AgGrid recommendation
    // to return empty strings: https://www.ag-grid.com/react-hooks/#react-null
    return <>{""}</>;
  }

  return (
    <div className={styles.container}>
      <Popover
        content={<RecordLocationMiniMap className={styles.map} entity={mappedEntity} />}
        destroyTooltipOnHide
        mouseEnterDelay={entity ? 1 : 0.1}
        placement="right"
        // transition name must be empty, otherwise map or map controls might be not displayed:
        // https://stackoverflow.com/a/64416167/5346779
        transitionName=""
      >
        {entity ? (
          <EntityChip
            entityId={entity.id}
            entityType={entity.entity}
            name={entity.name}
            onClick={handleClick}
          />
        ) : address ? (
          address
        ) : (
          <div className={styles.coordinates}>
            <FontAwesomeIcon icon={faMapMarkedAlt} />
            {formatCoordinates(geoPoint.coordinates)}
          </div>
        )}
      </Popover>
    </div>
  );
};

export default TripLocationCellRenderer;
